<template>
  <v-card>
    <v-card-text>
      <v-container fluid class="pa-2">
        <!-- Organizational Data -->
        <v-row align="center">
          <v-col>
            <h4>Organizational data</h4>
            <p>Enable organizational data tracking for this location.</p>
          </v-col>
          <v-col cols="auto">
            <v-checkbox
              v-model="registerSettings.metadata.organizationalData"
            />
          </v-col>
        </v-row>

        <!-- Shift System -->
        <v-row align="center">
          <v-col>
            <h4>Shift System</h4>
            <p>
              Allows you to open and close the till on a cash drawer. Invoices
              will not be allowed unless the shift/till is open. At the end of
              each shift the option to count the cash in the drawer is given.
              You can set up the minimum cash the shift should open with inside
              the register settings on the terminal.
            </p>
          </v-col>
          <v-col cols="auto">
            <v-checkbox v-model="registerSettings.shiftEnabled" />
          </v-col>
        </v-row>

        <!-- Shift Email Notification -->
        <v-row v-if="isShiftEnabled">
          <v-col>
            <v-row align="center">
              <v-col>
                <b>Send Notification on closing shift</b>
                <div>
                  Currently
                  <span>
                    {{
                      registerSettings.metadata?.closeShiftNotification?.emails
                        ?.length || 0
                    }}
                  </span>
                  Email(s) set.
                </div>
              </v-col>

              <v-col cols="auto" class="d-flex align-center">
                <v-btn
                  v-if="
                    registerSettings.metadata.closeShiftNotification.enabled
                  "
                  class="v-btn-with-radius mr-2"
                  color="primary"
                  dark
                  small
                  @click="openEmailDialog"
                >
                  <v-icon left small>mdi-pencil</v-icon>
                  Edit
                </v-btn>

                <v-btn
                  v-if="
                    !registerSettings.metadata.closeShiftNotification.enabled
                  "
                  class="v-btn-with-radius mr-2"
                  color="primary"
                  small
                  @click="switchShiftEmail"
                >
                  <v-icon left small>mdi-email-plus</v-icon>
                  Enable
                </v-btn>

                <v-btn
                  v-else
                  class="v-btn-with-radius"
                  color="error"
                  small
                  @click="switchShiftEmail"
                >
                  <v-icon left small>mdi-email-off</v-icon>
                  Disable
                </v-btn>
              </v-col>
            </v-row>

            <!-- Email Dialog -->
            <v-dialog persistent v-model="emailDialog" max-width="600px">
              <v-card rounded="xxl">
                <v-toolbar flat dense color="primary" dark>
                  <v-toolbar-title>Add Email</v-toolbar-title>
                  <v-spacer />
                  <v-btn icon @click="cancelDialog">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-container grid-list-lg>
                  <v-card-text>
                    <v-row align="center">
                      <v-col cols="12" sm="4">
                        <v-text-field
                          v-model="emailInfo.name"
                          label="Name"
                          :hide-details="nameErrors.length === 0"
                          :error-messages="nameErrors"
                          outlined
                          dense
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="emailInfo.email"
                          label="Email"
                          :hide-details="emailErrors.length === 0"
                          :error-messages="emailErrors"
                          outlined
                          dense
                        />
                      </v-col>
                      <v-col cols="2">
                        <v-btn
                          dark color="primary"
                          :disabled="$v.emailInfo.$invalid"
                          @click="addEmail"
                        >
                          <v-icon left small>mdi-plus</v-icon>
                          Add
                        </v-btn>
                      </v-col>
                    </v-row>

                    <v-list v-if="emailInfoList.length > 0" dense>
                      <v-divider />
                      <v-list-item
                        v-for="(row, index) in emailInfoList"
                        :key="index"
                        two-line
                      >
                        <v-list-item-content>
                          <v-list-item-title>{{ row.name }}</v-list-item-title>
                          <v-list-item-subtitle>{{
                            row.email
                          }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn icon color="error" @click="removeEmail(index)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider />
                    </v-list>
                  </v-card-text>
                </v-container>

                <v-card-actions class="justify-end">
                  <v-btn text  @click="cancelDialog">
                    Cancel
                  </v-btn>
                  <v-btn color="primary" @click="emailsUpdated"> Save </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>

        <!-- Location Data Reset -->
        <v-row>
          <v-col>
            <h4>Location Data Reset</h4>
            <p>
              Start this location fresh while choosing which operational data
              to remove. You will review exact record counts and retained data
              before anything is permanently deleted.
            </p>
            <v-btn
              outlined
              color="error"
              @click="resetDialog = true"
            >
              <v-icon left small>mdi-database-refresh</v-icon>
              Reset Location Data
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <!-- Footer buttons -->
    <v-card-actions class="pa-3 justify-end">
      <v-btn text color="secondary" @click="cancel"> Cancel </v-btn>
      <v-btn color="primary" dark @click="update">
        <v-icon left small>mdi-check</v-icon>
        Update
      </v-btn>
    </v-card-actions>

    <v-dialog v-model="resetDialog" max-width="760" persistent>
      <LocationResetDialog
        v-if="resetDialog"
        :LocationId="LocationId"
        :location="location"
        @close="resetDialog = false"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import locationService from "../service.js";
import LocationResetDialog from "./LocationResetDialog.vue";
import { validationMixin } from "vuelidate";
import { required, email } from "vuelidate/lib/validators";

export default {
  name: "location-settings",
  props: ["LocationId", "location"],
  components: { LocationResetDialog },
  mixins: [validationMixin],

  data() {
    return {
      registerSettings: {
        walkInCustomer: true,
        shiftEnabled: false,
        requireInventory: false,
        metadata: {
          organizationalData: false,
          closeShiftNotification: {
            enabled: false,
            emails: [],
          },
        },
      },
      resetDialog: false,
      emailDialog: false,
      emailInfo: { name: null, email: null },
      emailInfoList: [],
    };
  },

  created() {
    this.loadSettings();
  },

  computed: {
    nameErrors() {
      const errors = [];
      if (!this.$v.emailInfo.name.$dirty) return errors;
      if (!this.$v.emailInfo.name.required) errors.push("Field is required");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.emailInfo.email.$dirty) return errors;
      if (!this.$v.emailInfo.email.required) errors.push("Field is required");
      if (!this.$v.emailInfo.email.email) errors.push("Email is not valid");
      return errors;
    },
    isShiftEnabled() {
      return this.registerSettings?.shiftEnabled;
    },
  },

  validations: {
    emailInfo: {
      name: { required },
      email: { required, email },
    },
  },

  methods: {
    // async loadSettings() {
    //   this.$Progress.start();
    //   try {
    //     const res = await locationService.Settings.Register.get(
    //       this.LocationId
    //     );
    //     const settings = res.data;

    //     console.log("res setting", settings);
    //     this.registerSettings = { ...this.registerSettings, ...settings };
    //     if (!settings.metadata?.closeShiftNotification) {
    //       this.registerSettings.metadata.closeShiftNotification = {
    //         enabled: false,
    //         emails: [],
    //       };
    //     }
    //     this.emailInfoList =
    //       this.registerSettings.metadata.closeShiftNotification.emails;
    //     this.$Progress.finish();
    //   } catch (error) {
    //     this.$Progress.fail();
    //     this.$toasted.error(error?.data?.message || error.message, {
    //       position: "top-right",
    //       icon: "error",
    //       duration: 2000,
    //     });
    //   }
    // },
    async loadSettings() {
      this.$Progress.start();
      try {
        const res = await locationService.Settings.Register.get(
          this.LocationId
        );
        const settings = res.data;

        // 🛡 Defensive defaults
        if (!settings.metadata || typeof settings.metadata !== "object") {
          settings.metadata = {};
        }
        if (!settings.metadata.closeShiftNotification) {
          settings.metadata.closeShiftNotification = {
            enabled: false,
            emails: [],
          };
        }
        if (typeof settings.metadata.organizationalData === "undefined") {
          settings.metadata.organizationalData = false;
        }

        // Merge safely
        this.registerSettings = Object.assign(
          {},
          this.registerSettings,
          settings
        );

        // Keep local list updated
        this.emailInfoList =
          settings.metadata.closeShiftNotification.emails || [];

        this.$Progress.finish();
      } catch (error) {
        this.$Progress.fail();
        this.$toasted.error(error?.data?.message || error.message, {
          position: "top-right",
          icon: "error",
          duration: 2000,
        });
      }
    },
    openEmailDialog() {
      this.emailDialog = true;
    },
    cancelDialog() {
      this.reset();
      this.emailDialog = false;
    },

    reset() {
      this.emailInfo = { name: null, email: null };
      this.$v.$reset();
    },

    addEmail() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal.fire({
          toast: true,
          icon: "error",
          position: "top-end",
          title: "Invalid fields",
          timer: 3000,
        });
        return;
      }

      if (
        this.emailInfoList.some((row) => row.email === this.emailInfo.email)
      ) {
        this.$swal.fire({
          toast: true,
          icon: "error",
          position: "top-end",
          title: "Email already exists",
          timer: 3000,
        });
        return;
      }

      if (this.emailInfoList.length >= 5) {
        this.$swal.fire({
          toast: true,
          icon: "error",
          position: "top-end",
          title: "Cannot store more than 5 emails",
          timer: 3000,
        });
      } else {
        this.emailInfoList.push({ ...this.emailInfo });
        this.reset();
      }
    },

    removeEmail(index) {
      this.emailInfoList.splice(index, 1);
    },

    switchShiftEmail() {
      this.registerSettings.metadata.closeShiftNotification.enabled =
        !this.registerSettings.metadata.closeShiftNotification.enabled;
    },

    async emailsUpdated() {
      if (this.emailInfoList.length === 0) {
        this.$swal.fire({
          toast: true,
          icon: "error",
          position: "top-end",
          title: "Please add at least one email",
          timer: 3000,
        });
        return;
      }
      this.registerSettings.metadata.closeShiftNotification.emails =
        this.emailInfoList;
      this.update();
      this.cancelDialog();
    },

    cancel() {
      this.loadSettings();
    },

    async update() {
      this.$Progress.start();
      try {
        await locationService.Settings.Register.update(
          this.LocationId,
          this.registerSettings
        );
        this.$Progress.finish();
        this.$toasted.success("Setting was updated", {
          position: "top-right",
          icon: "check",
          duration: 2000,
        });
      } catch (error) {
        this.$Progress.fail();
        this.$toasted.error(error?.data?.message || error.message, {
          position: "top-right",
          icon: "error",
          duration: 2000,
        });
      }
    },
  },
};
</script>
